import { getPriceStatus } from '@toggle/helpers';
import clsx from 'clsx';
import React from 'react';

import { WithNAFallback } from '~/components/table';
import { Tooltip } from '~/components/tooltip/Tooltip';

import {
  PriceChangesChartLine,
  PriceChangesTooltipData,
  PriceChangesTooltipSort,
} from '../../PriceChangesChart.types';
import styles from './PriceChangesChartTooltip.module.scss';
import { formatDate, formatPercentChange, TOOLTIP_OFFSET_Y } from './utils';

type PriceChangesChartTooltipProps = {
  lines: PriceChangesChartLine[];
  tooltipData: PriceChangesTooltipData;
  formatter: (n: number) => string;
  sortBy?: PriceChangesTooltipSort;
  prevClosePrice?: number;
  locale?: string;
};

export const PriceChangesChartTooltip = ({
  lines,
  tooltipData,
  formatter,
  sortBy,
  prevClosePrice,
  locale = navigator.language,
}: PriceChangesChartTooltipProps) => {
  const isMultiAsset = lines.length > 1;

  const tooltipRows = lines.map(line => {
    const value = line.data.find(
      d => d.date.valueOf() === tooltipData.date.valueOf()
    )?.value;

    const pctChange =
      !isMultiAsset && prevClosePrice !== undefined && value !== undefined
        ? ((value - prevClosePrice) / prevClosePrice) * 100
        : undefined;

    return {
      color: line.color,
      label: line.label,
      value,
      isBenchmark: line.isBenchmark,
      pctChange,
    };
  });

  if (sortBy) {
    tooltipRows.sort((a, b) =>
      sortBy === 'desc'
        ? (b.value ?? 0) - (a.value ?? 0)
        : (a.value ?? 0) - (b.value ?? 0)
    );
  }

  const getTooltipContent = () => (
    <div
      className={clsx(styles.tooltipLabelContainer, {
        [styles.multiAsset]: isMultiAsset,
        [styles.withPercent]: !isMultiAsset && prevClosePrice !== undefined,
      })}
      data-testid="price-changes-chart-tooltip"
    >
      <div className={styles.dateRow}>
        <span className={styles.dateLabel}>
          {formatDate(tooltipData.date, locale)}
        </span>
      </div>
      {tooltipRows.map((row, index) => (
        <div
          key={row.label || index}
          className={styles.tooltipRow}
          data-testid="tooltip-row"
        >
          <div className={styles.tooltipLegend}>
            {isMultiAsset && (
              <div
                className={clsx(styles.tooltipLegendColor, {
                  [styles.dashed]: row.isBenchmark,
                })}
                style={
                  row.isBenchmark
                    ? { borderBottom: `1.5px dashed ${row.color}` }
                    : { backgroundColor: row.color }
                }
              />
            )}
            <span className={styles.tooltipLabel}>{row.label}</span>
          </div>
          <div
            className={clsx(styles.tooltipValueArea, {
              [styles.withPercentChange]: !isMultiAsset && prevClosePrice,
            })}
            data-testid="row-value-label"
          >
            <WithNAFallback
              value={
                row.value !== undefined ? (
                  <span
                    className={clsx(
                      styles.tooltipValue,
                      isMultiAsset && styles[getPriceStatus(row.value)]
                    )}
                  >
                    {`${isMultiAsset && row.value > 0 ? '+' : ''}${formatter(
                      row.value
                    )}`}
                  </span>
                ) : null
              }
              check={row.value !== undefined}
            />
            {row.pctChange !== undefined && (
              <span
                className={clsx(
                  styles.percentChange,
                  styles[getPriceStatus(row.pctChange)]
                )}
              >
                {formatPercentChange(row.pctChange)}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <Tooltip
      className={styles.chartTooltip}
      inPortal
      label={getTooltipContent()}
      isTouchDevice={false}
      isOpen
      hasArrow={false}
      dismissible={false}
      strategy="fixed"
      placement="top"
      offsetY={TOOLTIP_OFFSET_Y}
    >
      <div
        className={styles.tooltipRoot}
        style={{ left: `${tooltipData.xPos}px` }}
      />
    </Tooltip>
  );
};
